import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Popconfirm,
  message,
  Empty,
  Col,
  Row,
  Typography,
  Breadcrumb,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  LoadingOutlined,
  RetweetOutlined,
} from "@ant-design/icons";
import { translate } from "../../../locale";
import UserServices from "../../../services/UserServices";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { TableHelper } from "../../../helpers/TableHelper";

const { Title } = Typography;

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const PendingPatientInvitations = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
      total: 0,
    },
  });
  const [total, setTotal] = useState(0);

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record, index) => (
        <div className="flex flex-row space-x-4 justify-center items-center">
          <Popconfirm
            title={translate("¿Estás seguro de reenviar la invitación?")}
            okText={translate("Si")}
            cancelText={translate("No")}
            onConfirm={() => {
              handleResendInvitation(record);
            }}
          >
            <Tooltip title={translate("Reenviar invitación")}>
              <Button
                type="text"
                icon={<RetweetOutlined />}
                size="small"
                className="border-0"
              />
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title={translate("¿Estás seguro de cancelar la invitación?")}
            okText={translate("Si")}
            cancelText={translate("No")}
            onConfirm={() => {
              handleDelete(record.id);
            }}
          >
            <Tooltip title={translate("Borrar invitación")}>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                className="border-0"
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
      width: 100,
    },
    {
      title: translate("Email"),
      dataIndex: "email",
      key: "email",
      sorter: { multiple: 1 },
    },
    {
      title: "Estado",
      dataIndex: "expire_date",
      key: "expire_date",
      render: (_, record) => {
        const diff_sent = moment().diff(moment(record.register_date), "days");
        const diff_expiration = moment(record.expire_date).diff(
          moment(),
          "days"
        );
        if (diff_expiration < 1) {
          return (
            <div className="text-custom_darkred">{translate("Expirado")}</div>
          );
        }

        return (
          <>
            <div className="text-custom_darkgreen leading-none">
              {translate("Pendiente")}
            </div>
            <div className="text-xs text-custom_darkgray leading-none">
              {translate("(enviada hace {0} días)", diff_sent)}
            </div>
          </>
        );
      },
      sorter: { multiple: 1 },
    },
    {
      title: translate("Invitado por"),
      dataIndex: "full_name",
      key: "full_name",
      sorter: { multiple: 1 },
    },
  ];

  useEffect(() => {
    let data = {
      page_size: tableParams.pagination.pageSize,
      page_num: tableParams.pagination.current,
    };
    const sorter = TableHelper.sorter(tableParams);
    if (sorter !== null) {
      data = { ...data, order_by: sorter };
    }
    loadData(data);
  }, [JSON.stringify(tableParams), refresh]);

  const loadData = async (data) => {
    setLoading(true);
    try {
      const res = await UserServices.getInvitation(
        location.state.subscription.enterprise.id,
        location.state.subscription.id,
        data
      );
      if (
        res.status >= 200 &&
        res.status < 300 &&
        res.data.invites.length > 0
      ) {
        setData(
          res.data.invites.map((i) => ({
            ...i,
            full_name: i.emisor.full_name,
          }))
        );
        setTotal(res.data.total_pages * tableParams.pagination.pageSize);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleResendInvitation = async (item) => {
    setLoading(true);
    try {
      const data = {
        email: item.email,
        subscription: location.state.subscription.id,
        type: "email",
        rol: item.rol,
        app: "eira",
      };

      const res = await UserServices.sendInvitation(data);

      if (res) {
        if (res.data.message === "Mail Sent") {
          message.success(
            translate("La {0} se envió exitosamente.", translate("invitación"))
          );
        } else {
          message.error(res.data.message);
        }
        setLoading(false);
        setRefresh((v) => v + 1);
      } else {
        message.error(translate("No se pudieron guardar sus datos."));
      }
    } catch (e) {
      console.log(e);
      message.error(translate("La invitación no pudo ser enviada."));
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const res = await UserServices.deleteContactInvitation(id);
      if (res.status >= 200 && res.status < 300) {
        message.success(
          translate("La invitación ha sido eliminada exitsamente.")
        );
      } else {
        message.error(translate("La invitación no pudo ser eliminada."));
      }
      setRefresh((v) => v + 1);
      setLoading(false);
    } catch (error) {
      console.log(error);
      message.error(translate("La invitación no pudo ser eliminada."));
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  return (
    <div className="flex flex-col min-h-full w-screen max-w-7xl pb-16 m-10">
      <Row>
        <Col className="gutter-row" span="24">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/admin">{translate("Inicio")}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link
                to={".."}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(-1);
                }}
              >
                {translate("Suscripción")}
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {translate("Invitaciones pendientes")}
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>
        <Col span="24" className="my-4">
          <Title level={2} className="text-center">
            {translate("Invitaciones pendientes")}
          </Title>
        </Col>

        <Col span="24" className="mt-6">
          <Table
            rowKey={(i) => {
              return i.id.toString();
            }}
            dataSource={data}
            columns={columns}
            loading={
              loading
                ? { indicator: antIcon, className: "text-custom_blue" }
                : false
            }
            locale={{
              emptyText: (
                <Empty description={translate("Servicio aun no habilitado")} />
              ),
              sortTitle: translate("Ordenar"),
              triggerDesc: translate("Ordenar (descendente)"),
              triggerAsc: translate("Ordenar (ascendente)"),
              cancelSort: translate("Cancelar orden"),
            }}
            pagination={{ ...tableParams.pagination, total: total }}
            onChange={handleTableChange}
          />
        </Col>
      </Row>
    </div>
  );
};

export default PendingPatientInvitations;
